import React, { useState, useEffect } from 'react'
import { Modal, Button, Table, Badge, Spinner, Form } from 'react-bootstrap'
import { useMutation, useQuery, useLazyQuery } from '@apollo/client'
import { SYNC_MEMBER_TO_HEALTHSHERPA, SEARCH_HEALTHSHERPA_CONTACT } from '../../../gql/queries/policiesApiQueries'
import { GET_ALL_USERS_FILTER } from '../../../gql/queries/usersQuery'
import { IHealthSherpaContact } from '../../../types/policiesApi'
import { useGetUser } from '../../../store/userStore'
import { toast } from 'react-toastify'

type SearchField = 'birth_date' | 'phone' | 'email'

interface ModalHealthSherpaSearchProps {
  isOpen: boolean
  onClose: () => void
  member: {
    id: string
    firstName: string
    lastName: string
    dateBirth: string
    phone?: string | null
    email?: string | null
    healthsherpaContactId?: string | null
    address1?: string | null
    address2?: string | null
    city?: string | null
    state?: string | null
    zip?: string | null
    gender?: string | null
  }
  searchResults: IHealthSherpaContact[]
  onSuccess: (contactId: string) => void
  agentEmail: string
}

export const ModalHealthSherpaSearch: React.FC<ModalHealthSherpaSearchProps> = ({
  isOpen,
  onClose,
  member,
  searchResults,
  onSuccess,
  agentEmail,
}) => {
  const [syncMember, { loading }] = useMutation(SYNC_MEMBER_TO_HEALTHSHERPA)
  const [syncingId, setSyncingId] = useState<string | null>(null)
  const [currentAgentEmail, setCurrentAgentEmail] = useState(agentEmail)
  const [currentResults, setCurrentResults] = useState<IHealthSherpaContact[]>(searchResults)
  const [isSearching, setIsSearching] = useState(false)
  const [searchField, setSearchField] = useState<SearchField>('birth_date')
  const [selectedHsContactId, setSelectedHsContactId] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)

  const currentUser = useGetUser((s) => s.user?.user)
  const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
  const isAdmin = userGroups.includes('Administrador') || userGroups.includes('SuperAdministrador')
  const isAgent = userGroups.includes('Agente')
  const sessionAgentEmail = currentUser?.email || ''
  const isResyncMode = Boolean(member.healthsherpaContactId)

  const { data: usersData } = useQuery(GET_ALL_USERS_FILTER, {
    variables: { requireNpn: true },
    skip: !isOpen || !isAdmin,
  })
  const agentUsers: Array<{ id: string; firstName: string; lastName: string; email: string; npn: string }> =
    usersData?.allUsers?.edges?.map((e: any) => e.node) || []

  const [reSearch] = useLazyQuery(SEARCH_HEALTHSHERPA_CONTACT, {
    onCompleted: (data) => {
      setCurrentResults(data?.searchHealthsherpaContact?.contacts || [])
      setIsSearching(false)
    },
    onError: (err) => {
      toast.error(`Search error: ${err.message}`)
      setCurrentResults([])
      setIsSearching(false)
    },
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    setCurrentAgentEmail(agentEmail)
  }, [agentEmail])

  useEffect(() => {
    if (!isOpen || !isAgent || !sessionAgentEmail) return

    setCurrentAgentEmail(sessionAgentEmail)
  }, [isOpen, isAgent, sessionAgentEmail])

  useEffect(() => {
    if (!isOpen) {
      setIsInitialized(false)
      return
    }

    if (!isInitialized && isAdmin && sessionAgentEmail && agentUsers.length > 0) {
      const isInList = agentUsers.some((u) => u.email === sessionAgentEmail)
      if (isInList && !currentAgentEmail) {
        setCurrentAgentEmail(sessionAgentEmail)
      }
      setIsInitialized(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isInitialized])

  useEffect(() => {
    if (!isOpen) return
    setCurrentResults(searchResults)
    setSearchField('birth_date')
    setIsSearching(false)
    setSelectedHsContactId(null)
    setNotes('')
  }, [isOpen, searchResults, member.id])

  useEffect(() => {
    if (currentResults.length === 0) {
      setSelectedHsContactId(null)
      return
    }

    const existingMatch = member.healthsherpaContactId
      ? currentResults.find((contact) => contact.id === member.healthsherpaContactId)
      : null

    setSelectedHsContactId(existingMatch?.id || currentResults[0]?.id || null)
  }, [currentResults, member.healthsherpaContactId])

  const getSearchValue = () => {
    if (searchField === 'birth_date') return member.dateBirth
    if (searchField === 'phone') return member.phone
    return member.email
  }

  const handleSearch = () => {
    const selectedValue = getSearchValue()

    if (!currentAgentEmail) {
      toast.error('Select an agent before searching.')
      return
    }

    if (!selectedValue) {
      toast.error(`This member does not have ${searchField.replace('_', ' ')} available.`)
      return
    }

    setIsSearching(true)
    setCurrentResults([])
    reSearch({
      variables: {
        memberId: parseInt(member.id),
        agentEmail: currentAgentEmail,
        searchField,
      },
    })
  }

  const selectedHsContact = currentResults.find((contact) => contact.id === selectedHsContactId) || null

  const normalizePhoneForComparison = (value?: string | null) => {
    const digits = (value || '').replace(/\D/g, '')
    if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1)
    return digits
  }

  const normalizeValue = (value?: string | null) => (value || '').trim()

  const comparisonRows = selectedHsContact
    ? [
      {
        label: 'First Name',
        local: member.firstName || '—',
        hs: selectedHsContact.firstName || '—',
      },
      {
        label: 'Last Name',
        local: member.lastName || '—',
        hs: selectedHsContact.lastName || '—',
      },
      {
        label: 'Date of Birth',
        local: member.dateBirth || '—',
        hs: selectedHsContact.dateOfBirth || '—',
      },
      {
        label: 'Phone',
        local: member.phone || '—',
        hs: selectedHsContact.phone || '—',
      },
      {
        label: 'Email',
        local: member.email || '—',
        hs: selectedHsContact.email || '—',
      },
      {
        label: 'Address 1',
        local: member.address1 || '—',
        hs: selectedHsContact.address1 || '—',
      },
      {
        label: 'Address 2',
        local: member.address2 || '—',
        hs: selectedHsContact.address2 || '—',
      },
      {
        label: 'City',
        local: member.city || '—',
        hs: selectedHsContact.city || '—',
      },
      {
        label: 'State',
        local: member.state || '—',
        hs: selectedHsContact.state || '—',
      },
      {
        label: 'ZIP Code',
        local: member.zip || '—',
        hs: selectedHsContact.zip || '—',
      },
      {
        label: 'Sex',
        local: member.gender || '—',
        hs: selectedHsContact.gender || '—',
      },
    ].map((row) => ({
      ...row,
      isDifferent:
        row.label === 'Phone'
          ? normalizePhoneForComparison(row.local) !== normalizePhoneForComparison(row.hs)
          : normalizeValue(row.local) !== normalizeValue(row.hs),
    }))
    : []

  const differencesCount = comparisonRows.filter((row) => row.isDifferent).length

  const handleSync = async (contactId: string | null) => {
    setSyncingId(contactId || 'new')
    try {
      const { data } = await syncMember({
        variables: {
          memberId: parseInt(member.id),
          healthsherpaContactId: contactId,
          agentEmail: currentAgentEmail,
          notes: notes.trim() || null,
          resync: Boolean(contactId && isResyncMode),
        },
      })

      if (data?.syncMemberToMedicareHealthsherpa?.success) {
        toast.success(data.syncMemberToMedicareHealthsherpa.message || 'Sync successful')
        onSuccess(data.syncMemberToMedicareHealthsherpa.healthsherpaContactId)
        onClose()
      } else {
        toast.error(data?.syncMemberToMedicareHealthsherpa?.message || 'Sync failed')
      }
    } catch (error: any) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setSyncingId(null)
    }
  }

  return (
    <Modal show={isOpen} onHide={onClose} size="xl" centered backdrop="static">
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="text-primary font-weight-bold">
          <i className="flaticon-search-1 mr-2"></i>
          {isResyncMode ? 'HealthSherpa: Re-Sync' : 'HealthSherpa: Matches found'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {/* Local member */}
        <div className="mb-3 p-3 bg-light rounded border">
          <h6 className="text-muted mb-2 font-size-sm text-uppercase letter-spacing-1">Local Member:</h6>
          <div className="d-flex align-items-start">
            <div className="symbol symbol-40 symbol-light-primary mr-3 font-weight-bold">
              <span className="symbol-label font-size-h5">
                {member.firstName[0]}
                {member.lastName[0]}
              </span>
            </div>
            <div className="flex-grow-1">
              <div className="text-dark-75 font-weight-bolder font-size-lg">
                {member.firstName} {member.lastName}
              </div>
              <div className="mt-3 d-flex flex-wrap gap-3">
                <Form.Check
                  type="radio"
                  id={`hs-search-birth-date-${member.id}`}
                  name="hs-search-field"
                  label={`DOB: ${member.dateBirth || '—'}`}
                  inline
                  checked={searchField === 'birth_date'}
                  onChange={() => setSearchField('birth_date')}
                />
                <Form.Check
                  type="radio"
                  id={`hs-search-phone-${member.id}`}
                  name="hs-search-field"
                  label={`Phone: ${member.phone || '—'}`}
                  inline
                  checked={searchField === 'phone'}
                  onChange={() => setSearchField('phone')}
                />
                <Form.Check
                  type="radio"
                  id={`hs-search-email-${member.id}`}
                  name="hs-search-field"
                  label={`Email: ${member.email || '—'}`}
                  inline
                  checked={searchField === 'email'}
                  onChange={() => setSearchField('email')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Agent section */}
        <div className="mb-4 p-3 bg-light rounded border">
          <h6 className="text-muted mb-2 font-size-sm text-uppercase letter-spacing-1">Agent for this query:</h6>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="flex-grow-1">
              {isAdmin && agentUsers.length > 0 ? (
                <Form.Select
                  size="sm"
                  value={currentAgentEmail}
                  onChange={(e) => setCurrentAgentEmail(e.target.value)}
                  disabled={isSearching}
                >
                  {agentUsers.map((u) => (
                    <option key={u.id} value={u.email}>
                      {u.firstName} {u.lastName} — {u.email} (NPN: {u.npn})
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <div className="text-dark-75 font-weight-bold font-size-sm border rounded px-3 py-2 bg-white">
                  <i className="bi bi-person-badge me-1" />
                  {currentAgentEmail || '—'}
                </div>
              )}
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleSearch}
              disabled={isSearching || loading}
            >
              {isSearching ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Searching...
                </>
              ) : (
                <>
                  <i className="flaticon-search-1 mr-1"></i>
                  Search
                </>
              )}
            </Button>
          </div>
        </div>

        <p className="text-dark-50 mb-4">
          {isResyncMode
            ? "Review the HealthSherpa matches, compare them against the local record, and choose what you want to re-sync."
            : "The following HealthSherpa contacts match this member's information. Select one to link it, or create a new contact if none match."}
        </p>

        <div className="table-responsive">
          <Table hover className="border">
            <thead className="thead-light font-weight-bold text-muted text-uppercase font-size-sm">
              <tr>
                <th>Name in HealthSherpa</th>
                <th>DOB</th>
                <th>Phone</th>
                <th>Email</th>
                <th className="text-center">Status</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {isSearching ? (
                <tr>
                  <td colSpan={6} className="text-center py-5">
                    <Spinner animation="border" />
                  </td>
                </tr>
              ) : currentResults.length > 0 ? (
                currentResults.map((contact) => (
                  <tr
                    key={contact.id}
                    className="align-middle"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedHsContactId(contact.id)}
                  >
                    <td>
                      <div
                        className="d-flex flex-column"
                        style={{
                          padding: selectedHsContactId === contact.id ? '0.4rem 0.5rem' : undefined,
                          borderRadius: selectedHsContactId === contact.id ? '0.5rem' : undefined,
                          backgroundColor: selectedHsContactId === contact.id ? '#eef6ff' : 'transparent',
                          border: selectedHsContactId === contact.id ? '1px solid #b8d8ff' : '1px solid transparent',
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <div className="font-weight-bolder text-dark-75">{contact.firstName} {contact.lastName}</div>
                          {selectedHsContactId === contact.id && (
                            <Badge bg="primary">Selected</Badge>
                          )}
                        </div>
                        {member.healthsherpaContactId === contact.id && (
                          <div className="text-success font-size-xs mt-1 font-weight-bold">Currently linked in HS</div>
                        )}
                      </div>
                    </td>
                    <td>{contact.dateOfBirth}</td>
                    <td>{contact.phone || '—'}</td>
                    <td>{contact.email || '—'}</td>
                    <td className="text-center">
                      <Badge bg="info">
                        {contact.status || 'Active'}
                      </Badge>
                    </td>
                    <td className="text-right">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="font-weight-bolder px-4"
                        disabled={loading}
                        onClick={(event) => {
                          event.stopPropagation()
                          handleSync(contact.id)
                        }}
                      >
                        {syncingId === contact.id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          isResyncMode ? 'Re-Sync' : 'Link'
                        )}
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-5 text-muted italic">
                    No exact matches found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {selectedHsContact && (
          <div className="mt-4 p-3 bg-light rounded border">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-muted mb-0 font-size-sm text-uppercase letter-spacing-1">
                Local vs HealthSherpa
              </h6>
              <Badge bg={differencesCount > 0 ? 'warning' : 'success'} text={differencesCount > 0 ? 'dark' : undefined}>
                {differencesCount > 0 ? `${differencesCount} difference${differencesCount === 1 ? '' : 's'}` : 'No differences'}
              </Badge>
            </div>
            <Table bordered responsive size="sm" className="mb-0 bg-white">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Local</th>
                  <th>HealthSherpa</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.label} className={row.isDifferent ? 'table-warning' : ''}>
                    <td className="fw-bold">{row.label}</td>
                    <td>{row.local}</td>
                    <td>{row.hs}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <div className="mt-4 p-3 bg-light rounded border">
          <h6 className="text-muted mb-2 font-size-sm text-uppercase letter-spacing-1">Notes for HealthSherpa</h6>
          <Form.Control
            as="textarea"
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Add notes to send when creating a new HealthSherpa contact."
            disabled={loading || isSearching}
          />

        </div>
      </Modal.Body>
      <Modal.Footer className="bg-light d-flex justify-content-between">
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          className="font-weight-bolder px-5"
          disabled={loading || isSearching || (isResyncMode && !selectedHsContactId)}
          onClick={() => handleSync(isResyncMode ? selectedHsContactId : null)}
        >
          {syncingId === 'new' ? (
            <Spinner animation="border" size="sm" className="mr-2" />
          ) : (
            <i className={isResyncMode ? "bi bi-arrow-repeat mr-1" : "flaticon2-plus mr-1"}></i>
          )}
          {isResyncMode ? 'Re-Sync Selected Match' : 'Create New Contact'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
